import { useCallback, useRef, useState } from 'react';
import { Button } from 'antd';
import { LuCloudUpload, LuX } from 'react-icons/lu';
import styles from './DicomDropzone.module.css';

interface DicomDropzoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

const isAcceptable = (file: File): boolean => {
  const lower = file.name.toLowerCase();
  return (
    lower.endsWith('.dcm') ||
    lower.endsWith('.dicom') ||
    lower.endsWith('.zip') ||
    // Accept files with no extension — common for DICOM data
    !lower.includes('.')
  );
};

export default function DicomDropzone({
  files,
  onChange,
  disabled,
}: DicomDropzoneProps) {
  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    (incoming: File[]) => {
      const filtered = incoming.filter(isAcceptable);
      if (filtered.length === 0) return;
      onChange(filtered);
    },
    [onChange]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;
      const dropped = Array.from(e.dataTransfer.files);
      handleFiles(dropped);
    },
    [disabled, handleFiles]
  );

  const onSelectFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    handleFiles(selected);
    // Reset both inputs via their refs so re-selecting the same file fires
    // change again. Going through the ref avoids mutating the event object.
    if (filesInputRef.current) filesInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const removeAt = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.zone} ${isDragging ? styles.dragging : ''} ${
          disabled ? styles.disabled : ''
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <LuCloudUpload size={32} className={styles.icon} aria-hidden />
        <p className={styles.primaryLine}>
          Drop DICOM files or folders here, or browse from your computer.
        </p>
        <p className={styles.secondaryLine}>
          Studies will be grouped automatically.
        </p>
        <div className={styles.buttonRow}>
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => filesInputRef.current?.click()}
          >
            Select files
          </Button>
          <Button
            disabled={disabled}
            onClick={() => folderInputRef.current?.click()}
          >
            Browse folder
          </Button>
        </div>
        <p className={styles.hint}>
          Supports individual .dcm files, multi-file series, and ZIP-archived
          DICOM folders
        </p>
      </div>

      {/*
        Native <input type="file"> controls render a "Choose Files" button by
        default. Render them outside the visible flow with both display:none
        and visually-hidden positioning so the browser never paints them.
      */}
      <input
        ref={filesInputRef}
        type="file"
        multiple
        accept=".dcm,.dicom,.zip,application/dicom,application/zip"
        className={styles.hiddenInput}
        style={{ display: 'none' }}
        onChange={onSelectFiles}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        // @ts-expect-error -- non-standard folder picker; supported in
        // Chromium and WebKit; modern Firefox honors it via the same prop.
        webkitdirectory=""
        className={styles.hiddenInput}
        style={{ display: 'none' }}
        onChange={onSelectFiles}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
      />

      {files.length > 0 && (
        <ul className={styles.fileList}>
          {files.map((file, i) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className={styles.fileItem}
            >
              <span className={styles.fileName}>{file.name}</span>
              <span className={styles.fileSize}>
                {(file.size / 1024).toFixed(1)} KB
              </span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                className={styles.removeButton}
                onClick={() => removeAt(i)}
                disabled={disabled}
              >
                <LuX size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
